import { beginLoading, endLoading } from "@app/AppLayout/LoadingContext";
import { useApiHealthStore } from "@app/store/apiHealthStore";
import { ALLOCATION_SERVICE_HOST } from "./config";
import { ApiError, kindForStatus, SERVICE_UNREACHABLE_MESSAGE, toApiError } from "./ApiError";
import { getAccessToken, keycloak } from "./keycloak";
import { parseErrorMessage } from "./errorParser";

interface FetchDataOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  data?: any;
  silent?: boolean;
  contentType?: string;
  /**
   * Forbids the browser from answering from, or writing to, its HTTP cache. For the
   * queries that must reflect the database as it is right now, however often the same
   * URL is asked for - the class history search. Set through RequestInit rather than a
   * Cache-Control header, so no new header has to be allowed through CORS preflight.
   */
  noCache?: boolean;
}

export async function fetchData(
  endpoint: string,
  options: FetchDataOptions = {}
) {
  // Destructure with default values right in the function body
  const {
    method = 'GET',
    data,
    silent = false,
    contentType = 'application/json',
    noCache = false
  } = options;
    if (!silent) {
        beginLoading();
    }
    try {
        const url = `${ALLOCATION_SERVICE_HOST}${endpoint}`;
        let token: string;

        try {
            token = await getAccessToken();
        } catch (error) {
            keycloak.login();
            throw new ApiError("Your session has expired. Please sign in again.", { kind: 'auth', endpoint, cause: error });
        }

        const requestInit: RequestInit = {
            method,
            headers: {
                'Content-Type': contentType,
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: contentType === 'application/json' ? JSON.stringify(data) : data,
            ...(noCache ? { cache: 'no-store' as RequestCache } : {})
        };

        let response: Response;
        try {
            response = await fetch(url, requestInit);
        } catch (error) {
            // fetch only rejects when the request never got an answer: DNS, TLS,
            // refused connection, CORS preflight, offline browser.
            throw new ApiError(SERVICE_UNREACHABLE_MESSAGE, { kind: 'unreachable', endpoint, cause: error });
        }

        // Getting *any* answer proves the service is up, even a 404 or a 422 — with the
        // exception of gateway codes, which mean the proxy couldn't reach the backend.
        // Reported before the status checks so the liveness probe can recover from an
        // outage regardless of which status the probe endpoint returns.
        if (kindForStatus(response.status) !== 'unreachable') {
            useApiHealthStore.getState().reportSuccess();
        }

        if (!response.ok) {
            if (response.status === 401) {
                // Token was valid, but backend rejected it (roles, audience, etc)
                throw new ApiError("Unauthorized", { kind: 'auth', code: 401, endpoint });
            }
            const kind = kindForStatus(response.status);
            if (kind === 'unreachable') {
                // A gateway error body is a proxy's HTML page, not something to show a user
                throw new ApiError(SERVICE_UNREACHABLE_MESSAGE, { kind, code: response.status, endpoint });
            }

            const errorText = await response.text().catch(() => '');
            throw new ApiError(parseErrorMessage(errorText), { kind, code: response.status, endpoint });
        }

        if (response.status === 204) {
            return {};
        }

        return await response.json();
    } catch (error) {
        // Single choke point: every caller gets an ApiError, and the health store
        // learns about outages no matter which call happened to hit one first.
        const apiError = toApiError(error);
        useApiHealthStore.getState().reportFailure(apiError);
        throw apiError;
    } finally {
        if (!silent) {
            endLoading();
        }
    }
}

